import React from 'react';
import PropTypes from 'prop-types';
import Button from 'components/button/ActionButton';
import CSVFields, { mandatoryFields } from './settings';
import Dialog from 'components/ui/dialog/Dialog';
import DialogButton from 'components/button/DialogButton';
import SelectExportFields from './SelectExportFields';
import compose from 'recompose/compose';
import styled from 'styled-components';
import translate from 'components/hoc/translate';
import utils from 'helpers/utils';
import { ActivateIcon, AlertIcon, FileDownloadIcon } from 'assets/materialIcons/materialIcons';
import theme, { colors } from 'theme/theme';
import { connect } from 'react-redux';
import { exportReservation } from 'store/actions/reservations/exportReservations';

const Notification = styled.div`
    padding: 0 20px 10px 10px;
    background: ${ colors.harvestGold };
    border-radius: 5px;
    margin: 24px 0 0;
`;

const SubTitle = styled.div`
    font-size: 18px;
    padding: 10px 0;
`;

const dateRangeFilters = (filterInput) => {
    // A list of filters which contain a date range.
    const dateRangeKeys = [
        'cancelled_at',
        'created_at',
        'destination_arrival_date',
        'destination_departure_date',
        'updated_at',
    ];

    /**
     * We loop through the list and check if the property exists and if the type is a string.
     * then we will split the values and create an Array of it.
     */
    dateRangeKeys.forEach((key) => {
        if (filterInput.hasOwnProperty(key)) {
            if (typeof filterInput[key] === 'string') {
                filterInput[key] = filterInput[key].split('~');
            }
        }
    });

    return filterInput;
};

class DownloadCSV extends React.Component {
    state = {
        dialog: false,
        fields: CSVFields(),
        checkedFields: CSVFields(),
    };

    handleFieldToggle = (value, group) => {
        this.setState((prevState) => {
            const newGroup = [
                ...prevState.checkedFields[group],
            ];

            const index = newGroup.indexOf(value);

            if (~index) {
                // If found in the newGroup we'll remove it.
                newGroup.splice(index, 1);
            } else {
                // If not found in the newGroup we'll add it.
                newGroup.push(value);
            }

            return {
                checkedFields: {
                    ...prevState.checkedFields,
                    [group]: newGroup,
                },
            };
        });
    }

    toggleGroup = (value, group) => {
        this.setState({
            checkedFields: {
                ...this.state.checkedFields,
                [group]: value ? CSVFields()[group] : [],
            },
        });
    }

    handleDialogOpen = () => {
        this.setState({
            dialog: false,
        });
    };

    handleDialogClose = () => {
        this.setState({
            dialog: false,
        });
    };

    sendExport = () => {
        const { base, filterValues, exportReservation } = this.props;
        const fields = Object.values(this.state.checkedFields).flat();

        // This will merge withinthe selected filters, it's matchType and value to a cominbed value.
        let newFilters = utils.buildFilterObject(filterValues);

        // Adding the mandatoryfields to enable just exporting those fields.
        fields.push(...mandatoryFields);

        newFilters = dateRangeFilters(newFilters);

        return exportReservation({
            base: base,
            filters: newFilters,
            fields: fields,
        });
    }

    /**
     * If the total is more than the allowedMaxAmount, we will show a dialog.
     * Otherwise we will handle the call to send the export.
     */
    handleClick = (e) => {
        e.preventDefault();

        return this.setState({
            dialog: true,
        });
    }

    handleDialogSubmit = () => {
        this.handleDialogClose();

        this.sendExport();
    }

    render() {
        const { label, total, translate, allowedMaxAmount } = this.props;

        // Don't show if the total is still set to 0.
        if (total === 0) {
            return null;
        }

        const exceedsMaxAmount = total > allowedMaxAmount;

        const actions = [
            <DialogButton
                icon={ <AlertIcon /> }
                key='cancel'
                label={ translate('actions.cancel') }
                onTouchTap={ this.handleDialogClose }
                style={ { marginRight: '20px', marginTop: '24px' } }
            />,
            <DialogButton
                icon={ <ActivateIcon /> }
                key='submit'
                keyboardFocused={ true }
                label={ translate('actions.ok') }
                onTouchTap={ this.handleDialogSubmit }
                primary
            />,
        ];

        return (
            <span>
                <Button
                    icon={ <FileDownloadIcon color={ exceedsMaxAmount ? theme.palette.doveGray : theme.palette.primary1Color } /> }
                    title={ label }
                    onClick={ this.handleClick }
                    style={ { color: (exceedsMaxAmount ? theme.palette.doveGray : theme.palette.primary1Color) } }
                />

                <Dialog
                    autoScrollBodyContent={ true }
                    actions={ actions }
                    actionsContainerStyle={ { padding: '0 24px 24px' } }
                    contentStyle={ { maxWidth: '600px' } }
                    modal={ false }
                    overlayStyle={ {
                        backgroundColor: 'rgba(237, 236, 236, 0.6)',
                    } }
                    onRequestClose={ this.handleDialogClose }
                    open={ this.state.dialog }
                    repositionOnUpdate={ true }
                    title={ translate('export.title') }
                >
                    { exceedsMaxAmount &&
                        <Notification>
                            <SubTitle>
                                { translate('export.error.title') }
                            </SubTitle>

                            <p style={ {
                                margin: '0',
                                lineHeight: '1.4',
                            } }>{ translate('export.error.maximum_reached') }</p>
                        </Notification>
                    }

                    <SelectExportFields
                        checkedFields={ this.state.checkedFields }
                        fields={ this.state.fields }
                        onToggle={ () => {

                            /*
                             * Due to a bug which does not reposition the modal when toggling a nested list
                             * we need to force update the component in order to make sure it positions correctly.
                             *
                             * TODO: Investigate more elegant solutions for this issue.
                             */
                            this.forceUpdate();
                        } }
                        toggleField={ this.handleFieldToggle }
                        toggleGroup={ this.toggleGroup }
                    />
                </Dialog>
            </span>
        );
    }
}

DownloadCSV.defaultProps = {
    allowedMaxAmount: 15000,
};

DownloadCSV.propTypes = {
    allowedMaxAmount: PropTypes.number,
    base: PropTypes.string,
    filterValues: PropTypes.object,
    exportReservation: PropTypes.func.isRequired,
    label: PropTypes.string,
    total: PropTypes.number,
    translate: PropTypes.func,
};

const enhance = compose(
    translate,
    connect(
        null,
        {
            exportReservation: exportReservation,
        }
    ),
);

export default enhance(DownloadCSV);



// WEBPACK FOOTER //
// ./src/pages/reservations/list/actions/csv/ReservationCSVExport.js